import type { ReactNode } from 'react';

export type StatusTone = 'neutral' | 'info' | 'success' | 'danger' | 'warning' | 'accent';

const TONE_CLASSES: Record<StatusTone, string> = {
    accent: 'border-purple-700 bg-purple-900/50 text-purple-300',
    danger: 'border-red-700 bg-red-900/50 text-red-300',
    info: 'border-blue-700 bg-blue-900/50 text-blue-300',
    neutral: 'border-zinc-600 bg-zinc-700/50 text-zinc-300',
    success: 'border-green-700 bg-green-900/50 text-green-300',
    warning: 'border-yellow-700 bg-yellow-900/50 text-yellow-300'
};

const STATUS_TONES = new Map<string, StatusTone>([
    ['active', 'success'],
    ['archived', 'neutral'],
    ['cancelled', 'warning'],
    ['complete', 'success'],
    ['completed', 'success'],
    ['disabled', 'neutral'],
    ['draft', 'neutral'],
    ['error', 'danger'],
    ['failed', 'danger'],
    ['inactive', 'neutral'],
    ['not_applicable', 'neutral'],
    ['not_started', 'neutral'],
    ['passed', 'success'],
    ['paused', 'warning'],
    ['pending', 'accent'],
    ['queued', 'accent'],
    ['ready', 'success'],
    ['running', 'info'],
    ['stopped', 'warning'],
    ['succeeded', 'success'],
    ['superseded', 'warning'],
    ['warning', 'warning']
]);

interface StatusBadgeProps {
    /** Semantic status string, e.g. "running" or "not_started"; drives the default tone and label */
    status: string;
    /** Overrides the tone inferred from status */
    tone?: StatusTone;
    /** Overrides the auto-generated label (capitalized, underscores to spaces); children take precedence */
    children?: ReactNode;
    /** Gentle pulse for in-flight states (respects reduced-motion) */
    pulse?: boolean;
    className?: string;
}

const formatStatusLabel = (status: string): string => {
    const words = status.replace(/_/g, ' ');

    return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Consistent status pill for job/experiment/backtest lifecycle states,
 * active/inactive flags, draft/superseded markers, etc. Unknown statuses
 * render with the neutral tone; pass `tone` to override.
 */
const StatusBadge = ({ status, tone, children, pulse = false, className = '' }: StatusBadgeProps) => {
    const resolvedTone = tone ?? STATUS_TONES.get(status.toLowerCase()) ?? 'neutral';

    return (
        <span
            className={`inline-flex items-center whitespace-nowrap rounded border px-2 py-0.5 text-xs font-medium ${TONE_CLASSES[resolvedTone]} ${
                pulse ? 'motion-safe:animate-pulse' : ''
            } ${className}`}
        >
            {children ?? formatStatusLabel(status)}
        </span>
    );
};

export default StatusBadge;
